import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import { reportBuilderService } from '../../services/reportBuilderService';
import Stepper from '../../components/report-builder/wizard/Stepper';
import ScopingStep from '../../components/report-builder/wizard/ScopingStep';
import ModelSelectionStep from '../../components/report-builder/wizard/ModelSelectionStep';
import ColumnSelectionStep from '../../components/report-builder/wizard/ColumnSelectionStep';
import FiltersAndSortStep from '../../components/report-builder/wizard/FiltersAndSortStep';
import PreviewAndSaveStep from '../../components/report-builder/wizard/PreviewAndSaveStep';
import { ArrowLeft, CircleNotch, CloudArrowUp } from '@phosphor-icons/react';

const STEPS = [
  { label: 'Scope' },
  { label: 'Models' },
  { label: 'Columns' },
  { label: 'Filters' },
  { label: 'Preview' },
];

export default function ReportBuilderWizardPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const {
    selectedModelKeys, selectedDimensions, selectedMeasures, timeGroupings,
    scopingType, fetchModels, resetQuery, loadReportFromAPI,
  } = useReportBuilderStore();

  const [currentStep, setCurrentStep] = useState(0);
  const [reportName, setReportName] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditing && id) {
      setLoading(true);
      reportBuilderService.getSavedReport(id).then((report) => {
        setReportName(report.name);
        setReportDescription(report.description || '');
        // Hydrate the store from the saved report
        loadReportFromAPI(id).then(() => setLoading(false));
      }).catch(() => setLoading(false));
    } else {
      resetQuery();
      fetchModels();
    }
  }, [id, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0: return scopingType !== null;
      case 1: return selectedModelKeys.length > 0;
      case 2: return selectedDimensions.length > 0 || selectedMeasures.length > 0 || timeGroupings.length > 0;
      case 3: return true; // filters are optional
      case 4: return reportName.trim().length > 0;
      default: return false;
    }
  };

  const canGoNext = (): boolean => isStepValid(currentStep);

  /** Highest step the user can jump to (all preceding steps must be valid). */
  const maxReachableStep = (() => {
    let max = 0;
    for (let i = 0; i < STEPS.length; i++) {
      if (!isStepValid(i)) break;
      max = i + 1; // can advance past this step
    }
    return Math.min(max, STEPS.length - 1);
  })();

  const handleStepClick = (step: number) => {
    if (step <= maxReachableStep) setCurrentStep(step);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleSave = async () => {
    if (!reportName.trim()) return;
    setSaving(true);

    const store = useReportBuilderStore.getState();

    if (isEditing && id) {
      const config = await store.updateReportToAPI(id, reportName, reportDescription);
      setSaving(false);
      navigate(config ? `/analytics/report-builder/${config.id}` : '/analytics/report-builder');
    } else {
      const config = await store.saveReportToAPI(reportName, reportDescription);
      setSaving(false);
      navigate(config ? `/analytics/report-builder/${config.id}` : '/analytics/report-builder');
    }
  };

  const handleSaveAndPublish = async () => {
    if (!reportName.trim()) return;
    setSaving(true);

    const store = useReportBuilderStore.getState();

    if (isEditing && id) {
      const config = await store.updateReportToAPI(id, reportName, reportDescription);
      setSaving(false);
      navigate(config ? `/analytics/report-builder/${config.id}?publish=true` : '/analytics/report-builder');
    } else {
      const config = await store.saveReportToAPI(reportName, reportDescription);
      setSaving(false);
      navigate(config ? `/analytics/report-builder/${config.id}?publish=true` : '/analytics/report-builder');
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <CircleNotch size={32} className="animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/analytics/report-builder')}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">
            {isEditing ? `Edit: ${reportName || 'Report'}` : 'New Report'}
          </h1>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-4">
        <Stepper steps={STEPS} currentStep={currentStep} onStepClick={handleStepClick} maxClickableStep={maxReachableStep} />
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mx-auto max-w-4xl">
          {currentStep === 0 && <ScopingStep />}
          {currentStep === 1 && <ModelSelectionStep />}
          {currentStep === 2 && <ColumnSelectionStep />}
          {currentStep === 3 && <FiltersAndSortStep />}
          {currentStep === 4 && (
            <PreviewAndSaveStep
              name={reportName}
              description={reportDescription}
              onNameChange={setReportName}
              onDescriptionChange={setReportDescription}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-3">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <button
            onClick={() => navigate('/analytics/report-builder')}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
            )}
            {currentStep < STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={!canGoNext()}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            ) : (
              <>
                <button
                  onClick={handleSaveAndPublish}
                  disabled={!canGoNext() || saving}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? <CircleNotch size={16} className="animate-spin" /> : <CloudArrowUp size={16} />}
                  Save & Publish
                </button>
                <button
                  onClick={handleSave}
                  disabled={!canGoNext() || saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving && <CircleNotch size={16} className="animate-spin" />}
                  {isEditing ? 'Update Report' : 'Save Report'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
